import React, { useState, useEffect } from 'react';
import { 
  Save, Plus, Trash2, Layout, Type, Link as LinkIcon, 
  Settings, FolderEdit, CheckCircle, AlertCircle, RefreshCw 
} from 'lucide-react';

const AddSidebarItem = () => {
    const [existingMenus, setExistingMenus] = useState<any[]>([]);
    const [isNewGroup, setIsNewGroup] = useState(true);
    const [title, setTitle] = useState('');
    const [icon, setIcon] = useState('FaBox');
    const [subItems, setSubItems] = useState([{ name: '', path: '' }]);
    const [loading, setLoading] = useState(false);

    const fetchMenus = () => {
        fetch("/nodejs/api/sidebar/all")
            .then(res => res.json())
            .then(data => setExistingMenus(Array.isArray(data) ? data : []));
    };

    useEffect(() => { fetchMenus(); }, []);

    // Load Data for Editing
    const handleSelectExisting = (menuTitle: string) => {
        const m = existingMenus.find(x => x.title === menuTitle);
        if(m) {
            setTitle(m.title);
            setIcon(m.icon);
            // Remove prefix for easier editing
            const cleanSubItems = m.subItems.map((s:any) => ({
                name: s.name,
                path: s.path.replace('/frog-side/', '')
            }));
            setSubItems(cleanSubItems);
        }
    };

    const deleteGroup = async () => {
        if(!window.confirm(`Delete entire "${title}" group?`)) return;
        const res = await fetch(`nodejs/api/sidebar/delete/${title}`, { method: 'DELETE' });
        if(res.ok) { alert("Group Deleted!"); window.location.reload(); }
    };

    const saveMenu = async () => {
        setLoading(true);
        const formattedSubItems = subItems.map(item => ({
            name: item.name,
            path: item.path.startsWith('/frog-side/') ? item.path : `/frog-side/${item.path.replace(/^\//, '')}`
        }));

        const res = await fetch("/nodejs/api/sidebar/add", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ title, icon, subItems: formattedSubItems })
        });
        
        setLoading(false);
        if(res.ok) { 
            alert("Sidebar Updated!"); 
            window.location.reload(); 
        }
    };

    // Helper to switch modes
    const toggleMode = (mode: 'NEW' | 'EDIT') => {
        setIsNewGroup(mode === 'NEW');
        setTitle('');
        setIcon('FaBox');
        setSubItems([{ name: '', path: '' }]);
    };

    return (
        <div className="min-h-screen bg-white font-sans animate-fade-in-up">
            <div className="max-w-[1600px] mx-auto px-6 py-12">
                
                {/* Header */}
                <div className="mb-12 border-b border-gray-100 pb-6 flex items-center gap-5">
                    <div className="w-14 h-14 bg-[#006d5b]/10 rounded-2xl flex items-center justify-center text-[#006d5b] shadow-sm">
                        <Settings size={28} />
                    </div>
                    <div>
                        <h1 className="text-3xl font-black text-gray-900 tracking-tight">Sidebar Manager</h1>
                        <p className="text-gray-500 font-medium mt-1 text-sm">Configure navigation groups and page links.</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    
                    {/* LEFT: GROUP SETTINGS (4 Columns) */}
                    <div className="lg:col-span-4 space-y-8">
                        
                        {/* Mode Switcher */}
                        <div className="bg-gray-100 p-1 rounded-2xl flex font-black text-xs uppercase tracking-widest">
                            <button 
                                onClick={() => toggleMode('NEW')}
                                className={`flex-1 py-3 rounded-xl transition-all flex items-center justify-center gap-2 ${isNewGroup ? 'bg-white text-[#006d5b] shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                            >
                                <Plus size={14}/> Create New
                            </button>
                            <button 
                                onClick={() => toggleMode('EDIT')}
                                className={`flex-1 py-3 rounded-xl transition-all flex items-center justify-center gap-2 ${!isNewGroup ? 'bg-white text-[#006d5b] shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                            >
                                <FolderEdit size={14}/> Edit Group
                            </button>
                        </div>

                        {/* Configuration Card */}
                        <div className="bg-white rounded-[30px] border border-gray-200 shadow-xl shadow-gray-100/50 p-8 space-y-6">
                            <h3 className="text-sm font-black text-gray-800 uppercase tracking-widest flex items-center gap-2 mb-2">
                                <Layout size={16} className="text-[#006d5b]"/> Group Config
                            </h3>

                            {/* Title Selection */}
                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-2 block">Menu Title</label>
                                {isNewGroup ? (
                                    <input 
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-bold text-gray-900 focus:bg-white focus:border-[#006d5b] outline-none transition-all" 
                                        placeholder="e.g. Finance Module" 
                                        value={title} 
                                        onChange={e => setTitle(e.target.value)} 
                                    />
                                ) : (
                                    <div className="relative">
                                        <select 
                                            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-bold text-gray-700 appearance-none focus:bg-white focus:border-[#006d5b] outline-none cursor-pointer" 
                                            value={title} 
                                            onChange={e => handleSelectExisting(e.target.value)}
                                        >
                                            <option value="">-- Select Menu --</option>
                                            {existingMenus.map(m => <option key={m.id} value={m.title}>{m.title}</option>)}
                                        </select>
                                    </div>
                                )}
                            </div>

                            {/* Icon Name */}
                            <div>
                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1 mb-2 block">
                                    Icon Class Name <span className="text-gray-300">(React-Icons/Fa)</span>
                                </label>
                                <input 
                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-bold text-gray-900 focus:bg-white focus:border-[#006d5b] outline-none transition-all" 
                                    placeholder="e.g. FaUsers" 
                                    value={icon} 
                                    onChange={e => setIcon(e.target.value)} 
                                />
                            </div>

                            {/* Delete Action (Only Edit Mode) */}
                            {!isNewGroup && title && (
                                <div className="pt-4 border-t border-gray-100">
                                    <button 
                                        onClick={deleteGroup} 
                                        className="w-full bg-red-50 text-red-500 hover:bg-red-500 hover:text-white py-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2"
                                    >
                                        <Trash2 size={16} /> Delete Entire Group
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* RIGHT: PAGES MANAGER (8 Columns) */}
                    <div className="lg:col-span-8 space-y-6">
                        <div className="flex justify-between items-end mb-2">
                            <h3 className="text-sm font-black text-gray-800 uppercase tracking-widest">
                                Manage Sub-Pages
                            </h3>
                            <span className="text-[10px] font-bold text-gray-400 bg-gray-50 px-2 py-1 rounded-md">
                                {subItems.length} Pages
                            </span>
                        </div>

                        <div className="bg-white rounded-[30px] border border-gray-200 shadow-sm p-8 space-y-4">
                            {subItems.map((sub, i) => (
                                <div key={i} className="group flex flex-col md:flex-row items-center gap-4 p-4 rounded-2xl bg-white border border-gray-100 hover:border-[#006d5b]/30 hover:shadow-md transition-all">
                                    
                                    {/* Page Name */}
                                    <div className="flex-1 w-full relative">
                                        <Type className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                                        <input 
                                            className="w-full pl-11 pr-4 py-3 bg-gray-50 border-none rounded-xl font-bold text-sm text-gray-800 focus:bg-white focus:ring-2 focus:ring-[#006d5b]/10 outline-none transition-all placeholder:text-gray-400" 
                                            placeholder="Page Name (e.g. User List)" 
                                            value={sub.name} 
                                            onChange={e => {
                                                let s = [...subItems]; s[i].name = e.target.value; setSubItems(s);
                                            }} 
                                        />
                                    </div>

                                    {/* Page Path */}
                                    <div className="flex-[1.5] w-full relative">
                                        <div className="flex items-center bg-gray-50 rounded-xl px-4 py-3 border border-transparent focus-within:bg-white focus-within:ring-2 focus-within:ring-[#006d5b]/10 focus-within:border-[#006d5b]/20 transition-all">
                                            <LinkIcon className="text-gray-300 mr-2" size={16} />
                                            <span className="text-[#006d5b] font-bold text-xs whitespace-nowrap">/frog-side/</span>
                                            <input 
                                                className="flex-1 bg-transparent outline-none font-bold text-sm text-gray-700 ml-1 placeholder:text-gray-400" 
                                                placeholder="route-path" 
                                                value={sub.path} 
                                                onChange={e => {
                                                    let s = [...subItems]; s[i].path = e.target.value; setSubItems(s);
                                                }} 
                                            />
                                        </div>
                                    </div>

                                    {/* Remove Button */}
                                    <button 
                                        onClick={() => setSubItems(subItems.filter((_, idx) => idx !== i))} 
                                        className="p-3 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                                        title="Remove Page"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>
                            ))}

                            {/* Add Page Button */}
                            <button 
                                onClick={() => setSubItems([...subItems, { name: '', path: '' }])} 
                                className="w-full py-4 border-2 border-dashed border-gray-200 hover:border-[#006d5b] hover:bg-[#006d5b]/5 text-gray-400 hover:text-[#006d5b] rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2"
                            >
                                <Plus size={16} /> Add Another Page
                            </button>
                        </div>

                        {/* Save Action */}
                        <div className="flex justify-end pt-4">
                            <button 
                                onClick={saveMenu} 
                                disabled={loading}
                                className="bg-[#006d5b] hover:bg-[#005a4b] text-white px-10 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-[#006d5b]/20 flex items-center gap-3 transition-all active:scale-95 disabled:opacity-70"
                            >
                                {loading ? <RefreshCw className="animate-spin" size={18}/> : <Save size={18} />} 
                                Confirm Changes
                            </button>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default AddSidebarItem;